import { __awaiter, __generator } from "tslib";
import { fetch } from '../../lib/fetch';
/**
 * `HTTPCookieService.load(...)` should be awaited inside `loadAnalytics(...)`.
 * If the server can recreate an expired "Browser Cookie", by inspecting existing
 * "Server Cookies", we should delay dispatching events until receiving the Cookie.
 *
 *  dispatch$Method class methods should be used after any cookie interactions:
 * `dispatchCreate()` should be called after creating any new browser cookies.
 * `dispatchClear()` should be called after clearing any browser cookies.
 *
 * Manual `startQueueConsumer()` or `stopQueueConsumer()` is not usually necessary.
 *
 * Glossary:
 * "Server Cookies": `HTTPOnly:true`, stored on client, but only server can access.
 * "Browser Cookies": `HTTPOnly:false`, stored on client, and both client and server can access.
 */
var HTTPCookieService = /** @class */ (function () {
    function HTTPCookieService(options) {
        var _a, _b, _c;
        var urls = HTTPCookieService.urlHelper(options);
        this.renewUrl = urls.renewUrl;
        this.clearUrl = urls.clearUrl;
        this.backoff = (_a = options.backoff) !== null && _a !== void 0 ? _a : 300;
        this.retries = (_b = options.retries) !== null && _b !== void 0 ? _b : 3;
        this.flushInterval = (_c = options.flushInterval) !== null && _c !== void 0 ? _c : 1000;
        this.queue = [];
    }
    HTTPCookieService.urlHelper = function (options) {
        var origin = window.location.origin;
        return {
            renewUrl: new URL(options.renewUrl, origin).href,
            clearUrl: new URL(options.clearUrl, origin).href,
        };
    };
    HTTPCookieService.load = function (options) {
        return __awaiter(this, void 0, void 0, function () {
            var cookieService, req;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        cookieService = new HTTPCookieService(options);
                        req = cookieService.sendHTTPCookies(cookieService.renewUrl);
                        return [4 /*yield*/, retry(req, cookieService.retries, cookieService.backoff).catch(console.error)
                            // consume HTTPCookie actions, sequentially, as needed
                        ];
                    case 1:
                        _a.sent();
                        // consume HTTPCookie actions, sequentially, as needed
                        cookieService.startQueueConsumer();
                        return [2 /*return*/, cookieService];
                }
            });
        });
    };
    HTTPCookieService.prototype.dispatchCreate = function () {
        this.queue.push(this.sendHTTPCookies(this.renewUrl));
    };
    HTTPCookieService.prototype.dispatchClear = function () {
        this.queue.push(this.sendHTTPCookies(this.clearUrl));
    };
    HTTPCookieService.prototype.startQueueConsumer = function () {
        if (this.flushIntervalId) {
            console.error('HTTPCookie queue consumer is already running.');
            return;
        }
        var bound = this.consumeQueue.bind(this);
        this.flushIntervalId = setInterval(function () { return bound().catch(console.error); }, this.flushInterval);
    };
    HTTPCookieService.prototype.stopQueueConsumer = function () {
        if (!this.flushIntervalId) {
            console.error('HTTPCookie queue consumer is already stopped.');
            return;
        }
        clearInterval(this.flushIntervalId);
        this.flushIntervalId = undefined;
    };
    HTTPCookieService.prototype.sendHTTPCookies = function (serviceUrl) {
        return function () {
            return __awaiter(this, void 0, void 0, function () {
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4 /*yield*/, fetch(serviceUrl, {
                                credentials: 'include',
                                headers: {
                                    Accept: 'application/json',
                                    'Content-Type': 'application/json',
                                },
                                method: 'post',
                                body: JSON.stringify({
                                    sentAt: new Date().toISOString(),
                                }),
                            })];
                        case 1: return [2 /*return*/, _a.sent()];
                    }
                });
            });
        };
    };
    /**
     * This queue exists to avoid race conditions.
     *
     * Customer-developers may not `await` all promises.
     *
     * Therefore, introducing async code into analytics.track(), etc
     * could create race conditions in customer code.
     *
     * The queue enforces: if someone calls analytics.clear()
     * before calling analytics.identify(), the cookie service
     * will consume those actions, sequentially, even if no promises
     * are awaited.
     */
    HTTPCookieService.prototype.consumeQueue = function () {
        return __awaiter(this, void 0, void 0, function () {
            var req;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(this.queue.length > 0)) return [3 /*break*/, 2];
                        req = this.queue.shift();
                        return [4 /*yield*/, retry(req, this.retries, this.backoff).catch(console.error)];
                    case 1:
                        _a.sent();
                        return [3 /*break*/, 0];
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    return HTTPCookieService;
}());
export { HTTPCookieService };
function sleep(delayMS) {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, new Promise(function (resolve) { return setTimeout(resolve, delayMS); })];
        });
    });
}
function retry(req, retries, backoff) {
    return __awaiter(this, void 0, void 0, function () {
        var error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(retries >= 0)) return [3 /*break*/, 6];
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 5]);
                    return [4 /*yield*/, req().then(function (res) {
                            if (res.ok)
                                return res;
                            throw new Error("Status: ".concat(res.status, " ").concat(res.statusText));
                        })];
                case 2: return [2 /*return*/, _a.sent()];
                case 3:
                    error_1 = _a.sent();
                    retries -= 1;
                    if (retries <= 0)
                        throw error_1;
                    return [4 /*yield*/, sleep(backoff)];
                case 4:
                    _a.sent();
                    return [3 /*break*/, 5];
                case 5: return [3 /*break*/, 0];
                case 6: throw Error('HtEvents: Problem with DeferredRequest');
            }
        });
    });
}
//# sourceMappingURL=index.js.map